export const TEXT_CONNECTION_FONT_FAMILY = "Arial, sans-serif";

let measurementContext;

export const isTextConnectedField = (field) => {
  const connection = field?.text_connection;

  return Boolean(
    connection &&
      typeof connection === "object" &&
      connection.group_id &&
      connection.source_field_id &&
      field.field_id === connection.source_field_id &&
      Number.isInteger(connection.widget_index) &&
      connection.widget_index >= 0 &&
      Number.isInteger(connection.widget_count) &&
      connection.widget_count > connection.widget_index,
  );
};

export const getTextWidgetMetrics = (field) => {
  const width = Number(field?.rect?.W ?? field?.rect?.w ?? field?.width);

  const height = Number(field?.rect?.H ?? field?.rect?.h ?? field?.height);

  const savedFontSize = Number(field?.fontSize);
  const requestedFontSize =
    Number.isFinite(savedFontSize) && savedFontSize > 0 ? savedFontSize : 10;

  // Match FieldOverlay: 1px top padding.
  const contentHeight = height - 1;

  const isAutoSizedConnectedText =
    field?.autoFontSize === true &&
    isTextConnectedField(field) &&
    (Boolean(field?.is_generic) ||
      String(field?.field_id || "")
        .toLowerCase()
        .startsWith("generic_"));

  // Honor auto-sizing without bypassing the existing 10px minimum line height.
  const fontSize =
    isAutoSizedConnectedText &&
    Number.isFinite(contentHeight) &&
    contentHeight >= 10
      ? Math.min(requestedFontSize, contentHeight)
      : requestedFontSize;

  const lineHeight = Math.max(fontSize, 10);

  return {
    width,
    height,
    fontSize,
    lineHeight,

    // Match FieldOverlay: 2px left and right padding.
    contentWidth: width - 4,

    // PDF text widgets can render a single line when their saved font size
    // matches the widget height. Do not reject those fields solely because
    // the editor preview reserves a pixel of top padding.
    lineCount:
      Number.isFinite(contentHeight) && contentHeight > 0
        ? Math.max(1, Math.floor(contentHeight / lineHeight))
        : 0,
  };
};

const measureText = (text, fontSize) => {
  if (!measurementContext) {
    if (typeof document === "undefined") {
      throw new Error("Text measurement requires a browser.");
    }

    measurementContext = document.createElement("canvas").getContext("2d");
  }

  if (!measurementContext) {
    throw new Error("Unable to measure text.");
  }

  measurementContext.font = `${fontSize}px ${TEXT_CONNECTION_FONT_FAMILY}`;

  return measurementContext.measureText(text).width;
};

// Return one visual line and how much source text it consumed.
const takeLine = (remaining, metrics, measure) => {
  const newline = remaining.indexOf("\n");
  const paragraph = newline === -1 ? remaining : remaining.slice(0, newline);

  // Avoid splitting UTF-16 surrogate pairs.
  const characters = Array.from(paragraph);

  let low = 0;
  let high = characters.length;

  // Find the longest prefix that fits.
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const candidate = characters.slice(0, middle).join("");
    const width = measure(candidate, metrics.fontSize);

    if (!Number.isFinite(width) || width < 0) {
      throw new Error("Invalid text measurement.");
    }

    if (width <= metrics.contentWidth) {
      low = middle;
    } else {
      high = middle - 1;
    }
  }

  // The paragraph fits, including an intentionally empty line.
  if (low === characters.length) {
    return {
      text: paragraph,
      consumed: paragraph.length + (newline === -1 ? 0 : 1),
    };
  }

  if (low === 0) {
    throw new Error("A widget is too narrow to fit the next character.");
  }

  let count = low;

  // Prefer a word boundary when the width would split a word.
  if (!/\s/u.test(characters[count - 1]) && !/\s/u.test(characters[count])) {
    for (let index = count - 1; index > 0; index -= 1) {
      if (/\s/u.test(characters[index])) {
        count = index + 1;
        break;
      }
    }
  }

  // A word longer than the available width continues on the next line.
  const text = characters.slice(0, count).join("");

  return {
    text,
    consumed: text.length,
  };
};

export const splitTextAcrossWidgets = (
  widgets,
  input,
  { measure = measureText } = {},
) => {
  const ordered = [...widgets].sort(
    (a, b) => a.text_connection?.widget_index - b.text_connection?.widget_index,
  );

  const first = ordered[0]?.text_connection;

  // Never silently process an incomplete or mixed group.
  if (
    !first ||
    ordered.length !== first.widget_count ||
    ordered.some(
      (field, index) =>
        !isTextConnectedField(field) ||
        field.text_connection.group_id !== first.group_id ||
        field.text_connection.source_field_id !== first.source_field_id ||
        field.text_connection.widget_count !== ordered.length ||
        field.text_connection.widget_index !== index,
    )
  ) {
    throw new Error(
      "The text connection is incomplete. Select all widgets and reconnect them.",
    );
  }

  // Preserve exactly what the user entered.
  const sourceValue = String(input ?? "");

  // Normalize line endings and expand tabs only for rendering.
  let remaining = sourceValue.replace(/\r\n?/g, "\n").replace(/\t/g, "    ");

  const assignments = ordered.map((field) => {
    const metrics = getTextWidgetMetrics(field);

    if (
      !Number.isFinite(metrics.width) ||
      metrics.contentWidth <= 0 ||
      metrics.lineCount < 1
    ) {
      throw new Error(
        "A connected widget is too small for its font size. Resize it or adjust its font size.",
      );
    }

    const lines = [];

    for (let row = 0; row < metrics.lineCount && remaining.length; row += 1) {
      const line = takeLine(remaining, metrics, measure);

      lines.push(line.text);
      remaining = remaining.slice(line.consumed);
    }

    return {
      field,

      // Unused widgets receive an empty value.
      value: lines.join("\n"),
    };
  });

  return {
    sourceValue,
    assignments,

    // The caller must show a validation error when this is nonempty.
    overflow: remaining,
  };
};

const hasOwnValue = (object, key) =>
  Object.prototype.hasOwnProperty.call(object, key);

const collectTextSources = (fields, values = {}) => {
  const sources = new Map();

  fields.forEach((field) => {
    if (!isTextConnectedField(field)) return;

    const connection = field.text_connection;
    const sourceId = connection.source_field_id;

    if (!sources.has(sourceId)) {
      sources.set(sourceId, {
        id: sourceId,
        label:
          connection.source_field_label ||
          field.field_label ||
          field.label ||
          "Text field",
        groups: new Map(),
      });
    }

    const source = sources.get(sourceId);

    if (!source.groups.has(connection.group_id)) {
      source.groups.set(connection.group_id, []);
    }

    source.groups.get(connection.group_id).push(field);
  });

  sources.forEach((source) => {
    const widgets = [...source.groups.values()].flat();

    const saved = widgets.find((field) =>
      hasOwnValue(field.text_connection, "source_value"),
    );

    const firstGroup = [...source.groups.values()][0]
      .slice()
      .sort(
        (a, b) =>
          a.text_connection.widget_index - b.text_connection.widget_index,
      );

    const existingValues = firstGroup.map((field) => String(field.value ?? ""));

    const nonemptyValues = existingValues.filter((value) => value !== "");

    // Newly connected widgets may still contain the old repeated value.
    const fallback =
      new Set(nonemptyValues).size <= 1
        ? nonemptyValues[0] || ""
        : existingValues.join("\n");

    source.value = hasOwnValue(values, source.id)
      ? String(values[source.id] ?? "")
      : saved
        ? String(saved.text_connection.source_value ?? "")
        : fallback;
  });

  return sources;
};

export const applyConnectedTextValues = (
  fields = [],
  values = {},
  options = {},
) => {
  const sources = collectTextSources(fields, values);
  const replacements = new Map();
  const errors = new Map();

  sources.forEach((source) => {
    source.groups.forEach((widgets) => {
      let assignments;

      try {
        const result = splitTextAcrossWidgets(widgets, source.value, options);

        assignments = result.assignments;

        if (result.overflow) {
          errors.set(
            source.id,
            "The text exceeds the available space. Shorten it or add more connected widgets.",
          );
        }
      } catch (error) {
        errors.set(source.id, error.message);

        // Clear stale widget text while retaining the complete source input.
        assignments = widgets.map((field) => ({
          field,
          value: "",
        }));
      }

      assignments.forEach(({ field, value }) => {
        replacements.set(field, {
          ...field,
          value,
          text_connection: {
            ...field.text_connection,
            source_value: source.value,
          },
        });
      });
    });
  });

  return {
    sources,
    errors,
    fields: fields.map((field) => {
      if (replacements.has(field)) {
        return replacements.get(field);
      }

      const source = sources.get(field.field_id);

      // An ordinary widget with the same logical field still gets the full text.
      const isGeneric =
        Boolean(field?.is_generic) ||
        String(field?.field_id ?? "")
          .trim()
          .toLowerCase()
          .startsWith("generic_");

      return source && !isGeneric && !field.connection && !field.text_connection
        ? { ...field, value: source.value }
        : field;
    }),
  };
};

// These copies are for the right-hand form only.
// Raw PDF widgets retain their individual text values.
export const getTextConnectionDisplayFields = (
  fields = [],
  { measureText = false } = {},
) => {
  const { sources, errors } = measureText
    ? applyConnectedTextValues(fields)
    : {
        sources: collectTextSources(fields),
        errors: new Map(),
      };

  return fields.map((field) => {
    const source = sources.get(field.field_id);

    const isGeneric =
      Boolean(field?.is_generic) ||
      String(field?.field_id ?? "")
        .trim()
        .toLowerCase()
        .startsWith("generic_");

    if (
      !source ||
      field.connection ||
      (isGeneric && !isTextConnectedField(field))
    ) {
      return field;
    }

    return {
      ...field,
      name: getTextConnectionSourceName(source.id),
      label: source.label,
      field_label: source.label,
      type: "multiline",
      field_type: "multiline",
      value: source.value,
      text_connection_source: true,
      text_connection_error: errors.get(source.id) || "",
    };
  });
};

export const getTextConnectionSourceName = (sourceFieldId) =>
  `connected_text_${sourceFieldId}`;

export const applyPersistedConnectedTextValues = (fields = [], values = {}) => {
  const next = { ...values };
  const restoredSourceIds = new Set();

  fields.forEach((field) => {
    if (!isTextConnectedField(field)) return;

    const connection = field.text_connection;
    const sourceId = connection.source_field_id;

    if (
      restoredSourceIds.has(sourceId) ||
      !hasOwnValue(connection, "source_value")
    ) {
      return;
    }

    next[getTextConnectionSourceName(sourceId)] = String(
      connection.source_value ?? "",
    );
    restoredSourceIds.add(sourceId);
  });

  return next;
};

export const getConnectedTextValidationErrors = (
  fields = [],
  formikValues = {},
  options = {},
) => {
  const sourceValues = {};

  fields.forEach((field) => {
    if (!isTextConnectedField(field)) return;

    const sourceId = field.text_connection.source_field_id;
    const inputName = getTextConnectionSourceName(sourceId);

    // Use the current input, including an intentional empty string.
    if (hasOwnValue(formikValues, inputName)) {
      sourceValues[sourceId] = String(formikValues[inputName] ?? "");
    }
  });

  // Build fresh errors on every validation; never retain old overflow errors.
  const errors = {};
  const sources = collectTextSources(fields, sourceValues);

  sources.forEach((source) => {
    const inputName = getTextConnectionSourceName(source.id);

    // Existing form validation handles whether this field is required.
    if (source.value === "") return;

    // The text must fit every connected group displaying this source.
    for (const widgets of source.groups.values()) {
      const missingDimensions = widgets.some((field) => {
        const { width, height } = getTextWidgetMetrics(field);

        return !Number.isFinite(width) || !Number.isFinite(height);
      });

      if (missingDimensions) {
        errors[inputName] =
          "Cannot check text fit because connected field dimensions are missing.";
        break;
      }

      try {
        const { overflow } = splitTextAcrossWidgets(
          widgets,
          source.value,
          options,
        );

        if (overflow !== "") {
          errors[inputName] =
            "The text exceeds the available space. Shorten it or add more connected widgets.";
          break;
        }
      } catch (error) {
        errors[inputName] =
          error?.message || "Unable to check the connected text fields.";
        break;
      }
    }
  });

  return errors;
};

export const resolveConnectedTextSubmission = (
  fields = [],
  formikValues = {},
  fieldNamesToOverlay = null,
) => {
  const connectedFields = fields.filter(isTextConnectedField);

  if (!connectedFields.length) return [];

  const sourceIds = new Set(
    connectedFields.map((field) => field.text_connection.source_field_id),
  );

  const selectedSourceIds = new Set();
  const sourceValues = {};
  const errors = {};

  sourceIds.forEach((sourceId) => {
    const inputName = getTextConnectionSourceName(sourceId);
    const widgets = connectedFields.filter(
      (field) => field.text_connection.source_field_id === sourceId,
    );

    if (fieldNamesToOverlay) {
      const includesSource = fieldNamesToOverlay.has(inputName);

      const includesAllWidgets = widgets.every(
        (field) => field.name && fieldNamesToOverlay.has(field.name),
      );

      const includesSomeWidgets = widgets.some(
        (field) => field.name && fieldNamesToOverlay.has(field.name),
      );

      if (!includesSource && !includesAllWidgets) {
        if (includesSomeWidgets) {
          errors[inputName] =
            "Update the complete connected text field together.";
        }

        return;
      }
    }

    selectedSourceIds.add(sourceId);

    if (hasOwnValue(formikValues, inputName)) {
      sourceValues[sourceId] = String(formikValues[inputName] ?? "");
    }
  });

  const sources = collectTextSources(fields, sourceValues);

  selectedSourceIds.forEach((sourceId) => {
    sources.get(sourceId).groups.forEach((widgets) => {
      const ordered = [...widgets].sort(
        (a, b) =>
          a.text_connection.widget_index - b.text_connection.widget_index,
      );

      const isIncomplete = ordered.some(
        (field, index) =>
          field.text_connection.widget_count !== ordered.length ||
          field.text_connection.widget_index !== index,
      );

      if (isIncomplete) {
        errors[getTextConnectionSourceName(sourceId)] =
          "The text connection is incomplete. All connected widgets must be included.";
      }
    });
  });

  if (Object.keys(errors).length) {
    const error = new Error([...new Set(Object.values(errors))].join("\n"));

    error.textConnectionErrors = errors;
    throw error;
  }

  return fields
    .filter(
      (field) =>
        selectedSourceIds.has(field.field_id) &&
        !field.connection &&
        (isTextConnectedField(field) ||
          !fieldNamesToOverlay ||
          fieldNamesToOverlay.has(field.name)),
    )
    .map((field) => {
      const sourceValue = sources.get(field.field_id).value;

      return {
        ...field,

        // Backend distributes this full text using saved widget geometry.
        value: sourceValue,

        ...(isTextConnectedField(field)
          ? {
              text_connection: {
                ...field.text_connection,
                source_value: sourceValue,
              },
            }
          : {}),
      };
    });
};

export const cleanupMultilineConnectionsAfterRetag = (
  previousFile,
  nextFile,
) => {
  const before = previousFile?.document?.tempFields;
  const after = nextFile?.document?.tempFields;

  if (
    !Array.isArray(before?.formFields) ||
    !Array.isArray(after?.formFields) ||
    before.formFields === after.formFields
  ) {
    return nextFile;
  }

  // Tagging in these panels identifies widgets by name.
  const nextByName = new Map(
    after.formFields.map((field) => [field.name, field]),
  );

  const groupsToDisconnect = new Set();

  before.formFields.forEach((oldField) => {
    const groupId = oldField?.text_connection?.group_id;
    if (!groupId || !oldField.name) return;

    const nextField = nextByName.get(oldField.name);

    if (
      !nextField ||
      (oldField.field_id ?? "") !== (nextField.field_id ?? "")
    ) {
      groupsToDisconnect.add(groupId);
    }
  });

  if (!groupsToDisconnect.size) return nextFile;

  const formFields = after.formFields.map((field) => {
    if (!groupsToDisconnect.has(field?.text_connection?.group_id)) {
      return field;
    }

    const cleanedField = { ...field };
    delete cleanedField.text_connection;
    return cleanedField;
  });

  const cleanedByName = new Map(formFields.map((field) => [field.name, field]));

  const syncSelectedField = (field) =>
    field ? (cleanedByName.get(field.name) ?? field) : field;

  return {
    ...nextFile,
    document: {
      ...nextFile.document,
      tempFields: {
        ...after,
        formFields,
        selectedFormField: syncSelectedField(after.selectedFormField),
        selectedExtraFields: Array.isArray(after.selectedExtraFields)
          ? after.selectedExtraFields.map(syncSelectedField)
          : after.selectedExtraFields,
      },
    },
  };
};
